import { memo } from 'react'
import { Button } from '@/components/ui/button'
import { MapPin, MoreVertical, Plus, RotateCw } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Position } from '../types'

type Coordinate = {
  x: number
  y: number
}

interface PositionCoordinateListPanelProps {
  selectedCoordinate: Coordinate | null
  positions: Position[]
  selectedPositionId: string
  isDeletingLocation?: boolean
  onSelectPosition: (positionId: string) => void
  onUpdatePosition?: (positionId: string) => void
  onRequestDelete: (positionId: string) => void
  onRefresh?: () => void
  onAddPosition?: (positionId: string) => void
}

const PositionRowItem = memo(
  ({
    pos,
    isSelected,
    isDeletingLocation,
    onSelectPosition,
    onUpdatePosition,
    onRequestDelete,
  }: {
    pos: Position
    isSelected: boolean
    isDeletingLocation: boolean
    onSelectPosition: (id: string) => void
    onUpdatePosition?: (id: string) => void
    onRequestDelete: (id: string) => void
  }) => {
    return (
      <div
        className={`group flex items-center justify-between rounded-xl px-3 py-2.5 transition-all duration-200 ring-1 m-1 ${
          isSelected
            ? 'bg-[#fff7f3] ring-[#CE7A58]/60 shadow-sm shadow-[#CE7A58]/5'
            : 'ring-gray-100 hover:bg-gray-50'
        }`}
      >
        <button
          type="button"
          onClick={() => onSelectPosition(pos.id)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left justify-start bg-transparent border-none outline-none cursor-pointer p-0"
        >
          <MapPin
            className={`h-4 w-4 shrink-0 ${isSelected ? 'text-[#CE7A58]' : 'text-gray-400'}`}
          />
          <span
            className={`truncate text-sm font-medium ${isSelected ? 'text-[#111827]' : 'text-gray-700'}`}
          >
            {pos.name || 'Tên vị trí'}
          </span>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 rounded-lg text-gray-500 hover:bg-gray-100 data-[state=open]:bg-gray-100"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-28 rounded-lg p-1">
            {pos.id !== 'home' && (
              <DropdownMenuItem
                onClick={() => onUpdatePosition?.(pos.id)}
                className="text-sm cursor-pointer text-gray-700 focus:text-[#CE7A58] focus:bg-[#fff7f3]"
              >
                Cập nhật
              </DropdownMenuItem>
            )}
            {pos.id !== 'home' && (
              <DropdownMenuItem
                onClick={() => {
                  if (isDeletingLocation) return
                  onRequestDelete(pos.id)
                }}
                className={`text-sm cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50 ${
                  isDeletingLocation
                    ? 'opacity-40 cursor-not-allowed pointer-events-none'
                    : ''
                }`}
              >
                Xóa
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    )
  },
)

PositionRowItem.displayName = 'PositionRowItem'

export function PositionCoordinateListPanel({
  selectedCoordinate,
  positions,
  selectedPositionId,
  isDeletingLocation = false,
  onSelectPosition,
  onUpdatePosition,
  onRequestDelete,
  onRefresh,
  onAddPosition,
}: PositionCoordinateListPanelProps) {
  return (
    <div className="flex w-full shrink-0 flex-col rounded-2xl ring-1 ring-gray-300 bg-white p-4 shadow-sm xl:w-[320px]">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[18px] font-bold text-[#1f2937]">
            Danh sách vị trí
          </div>
          <div className="text-[12px] text-gray-400 mt-0.5">
            {selectedCoordinate
              ? `${selectedCoordinate.x.toFixed(5)}, ${selectedCoordinate.y.toFixed(5)}`
              : 'Chưa chọn tọa độ'}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            className="h-8 w-8 rounded-full border-[#e2a288]/30 text-[#CE7A58] hover:bg-[#fff7f3] hover:text-[#CE7A58]"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
          <Button
            variant="primary"
            size="icon"
            onClick={() => onAddPosition?.('new')}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Danh sách các vị trí */}
      {positions.length === 0 ? (
        <div className="rounded-xl border border-dashed bg-[#f6f7fb] p-6 text-center text-sm text-gray-400">
          Không có vị trí nào tại tọa độ này
        </div>
      ) : (
        <div className="flex max-h-[500px] flex-col gap-1 overflow-y-auto pr-1">
          {positions.map((pos) => (
            <PositionRowItem
              key={pos.id}
              pos={pos}
              isSelected={selectedPositionId === pos.id}
              isDeletingLocation={isDeletingLocation}
              onSelectPosition={onSelectPosition}
              onUpdatePosition={onUpdatePosition}
              onRequestDelete={onRequestDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}
